"use strict";
// Copyright © Veeam Software Group GmbH
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Form = void 0;
const fast_deep_equal_1 = __importDefault(require("fast-deep-equal"));
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const codes_1 = require("./rules/codes");
const VALIDATION_ERRORS = {
    [codes_1.VALIDATE_CODES.default]: 'The value in this field is invalid',
    [codes_1.VALIDATE_CODES.required]: 'The field is required',
    [codes_1.VALIDATE_CODES.maxLength]: 'The maximum length is {{length}}',
    [codes_1.VALIDATE_CODES.minLength]: 'The minimum length is {{length}}',
    [codes_1.VALIDATE_CODES.maxNumber]: 'The maximum number is {{max}}',
    [codes_1.VALIDATE_CODES.minNumber]: 'The minimum number is {{min}}'
};
class FieldState {
    constructor() {
        this.state = {};
        this.subject = new rxjs_1.BehaviorSubject({});
    }
    asObservable() {
        return this.subject.asObservable();
    }
    getValue() {
        return this.state;
    }
    update(key, value) {
        this.state[key] = value;
        this.subject.next(this.state);
    }
}
class Form {
    constructor(model, config = {}) {
        this.model = model;
        this.config = config;
        this.errorState = new FieldState();
        this.displayErrorState = new FieldState();
        this.additionalValidationRules = {};
        this.internalInitialValue = null;
        this.compare = fast_deep_equal_1.default;
        this.makeInitSubject = new rxjs_1.Subject();
        if (config.comparator) {
            this.compare = config.comparator;
        }
        try {
            this.internalInitialValue = this.model.getValue();
            this.validate();
        }
        catch (_a) { }
    }
    hasErrors() {
        const modelValue = this.model.getValue();
        const errors = this.errorState.getValue();
        for (const key in errors) {
            if (!modelValue.hasOwnProperty(key)) {
                continue;
            }
            if (errors[key]) {
                return true;
            }
        }
        return false;
    }
    validateField(key) {
        const schema = this.config.validationSchema;
        const schemaRules = schema && Array.isArray(schema[key]) ? schema[key] : [];
        const additionalRules = this.additionalValidationRules[key] || [];
        let rules = Array.isArray(schemaRules) ? [...schemaRules] : [];
        if (Array.isArray(additionalRules)) {
            rules = rules.concat(additionalRules);
        }
        let err = '';
        const modelValue = this.model.getValue();
        rules.some((rule) => {
            err = rule(modelValue[key], modelValue, VALIDATION_ERRORS) || '';
            return err;
        });
        this.setError(key, err);
        return !err;
    }
    makeValueInitial() {
        this.internalInitialValue = this.model.getValue();
        this.makeInitSubject.next(true);
    }
    get fields() {
        if (this.internalFields) {
            return this.internalFields;
        }
        const newObj = {};
        const modelValue = this.model.getValue();
        for (const key in modelValue) {
            if (!modelValue.hasOwnProperty(key)) {
                continue;
            }
            newObj[key] = {
                getName: () => key,
                getValue: () => {
                    return this.model.getValue()[key];
                },
                asObservable: () => {
                    return this.model.fieldChanges(key).pipe((0, operators_1.map)((v) => v.value));
                },
                update: (newValue) => {
                    if (this.model.update && newValue !== this.model.getValue()[key]) {
                        this.hideError(key);
                        this.model.update((value) => {
                            value[key] = newValue;
                        });
                    }
                },
                touch: () => {
                    this.validateField(key);
                    this.displayError(key);
                },
                getMeta: () => {
                    return {
                        displayError: !!this.displayErrorState.getValue()[key]
                    };
                },
                getMetaObservable: () => {
                    return this.displayErrorState.asObservable().pipe((0, operators_1.skip)(1), (0, operators_1.map)(() => !!this.displayErrorState.getValue()[key]), (0, operators_1.distinctUntilChanged)(), (0, operators_1.map)((value) => ({ displayError: value })));
                },
                getError: () => {
                    return String(this.errorState.getValue()[key] || '');
                },
                getErrorObservable: () => {
                    return this.errorState.asObservable().pipe((0, operators_1.skip)(1), (0, operators_1.map)(() => {
                        return String(this.errorState.getValue()[key] || '');
                    }), (0, operators_1.distinctUntilChanged)());
                },
                setError: (error) => {
                    this.setError(key, error);
                },
                addValidationRule: (rule) => {
                    var _a;
                    if (!this.additionalValidationRules[key]) {
                        this.additionalValidationRules[key] = [];
                    }
                    (_a = this.additionalValidationRules[key]) === null || _a === void 0 ? void 0 : _a.push(rule);
                    this.validateField(key);
                    return () => {
                        var _a, _b;
                        if (Array.isArray(this.additionalValidationRules[key])) {
                            this.additionalValidationRules[key] = (_b = (_a = this.additionalValidationRules) === null || _a === void 0 ? void 0 : _a[key]) === null || _b === void 0 ? void 0 : _b.filter((i) => i !== rule);
                            this.validateField(key);
                        }
                    };
                },
                resetValidationRules: () => {
                    this.additionalValidationRules[key] = [];
                }
            };
        }
        this.internalFields = newObj;
        return this.internalFields;
    }
    displayError(key, value) {
        if (typeof value === 'string') {
            this.setError(key, value);
        }
        this.displayErrorState.update(key, true);
    }
    hideError(key) {
        this.displayErrorState.update(key, false);
    }
    getValue() {
        return this.model.getValue();
    }
    validate() {
        const modelValue = this.getValue();
        for (const key in modelValue) {
            if (!modelValue.hasOwnProperty(key)) {
                continue;
            }
            this.validateField(key);
        }
        return !this.hasErrors();
    }
    validateAndDisplayErrors() {
        const result = this.validate();
        this.displayErrors();
        return result;
    }
    displayErrors() {
        const modelValue = this.getValue();
        for (const key in modelValue) {
            if (!modelValue.hasOwnProperty(key)) {
                continue;
            }
            this.displayError(key);
        }
    }
    setError(key, value) {
        if (this.errorState.getValue()[key] === value) {
            return;
        }
        this.errorState.update(key, value);
    }
    hasChanges() {
        return !this.compare(this.internalInitialValue, this.getValue());
    }
    getMetaValue() {
        return {
            isValid: this.validate(),
            hasChanges: this.hasChanges()
        };
    }
    metaChanges() {
        const getMetaValue = () => {
            return {
                isValid: !this.hasErrors(),
                hasChanges: this.hasChanges()
            };
        };
        return (0, rxjs_1.merge)(this.model.asObservable().pipe((0, operators_1.tap)(() => this.validate())), this.errorState.asObservable(), this.makeInitSubject.asObservable()).pipe((0, operators_1.startWith)(getMetaValue()), (0, operators_1.map)(() => getMetaValue()), (0, operators_1.distinctUntilChanged)(fast_deep_equal_1.default), (0, operators_1.scan)((acc, curr) => {
            return {
                value: curr,
                prevValue: acc.value
            };
        }, {}), (0, operators_1.skip)(1));
    }
    resetToInitialValue(key) {
        if (this.internalInitialValue === null) {
            return;
        }
        if (key === undefined) {
            this.model.setValue(this.internalInitialValue);
            return;
        }
        if (this.internalInitialValue.hasOwnProperty(key)) {
            const initialValue = this.internalInitialValue[key];
            this.model.update((value) => {
                value[key] = initialValue;
            });
        }
    }
    hasChange(key) {
        var _a;
        return !this.compare((_a = this.internalInitialValue) === null || _a === void 0 ? void 0 : _a[key], this.getValue()[key]);
    }
    getInitialValue(key) {
        if (!this.internalInitialValue) {
            return null;
        }
        return this.internalInitialValue[key];
    }
}
exports.Form = Form;
//# sourceMappingURL=Form.js.map